import { Platform } from 'react-native';

import { getAnalyticsEnvironment } from '../../analytics/environment.helper';
import { getWebAnalyticsConsent } from '../../privacy/helpers/webAnalyticsConsent.helper';
import { WEB_ANALYTICS_CONSENT_DENIED } from '../../privacy/privacyConsent.constants';
import { LAUNCH_PHASE } from '../constants/launchDiagnostics.constants';
import { logLaunchPhase } from './launchDiagnostics.helper';
import { IOS_DISABLED_NATIVE_SDKS } from '../constants/nativeSdkInit.constants';
import { getNativeCrashReportingDsn } from '../constants/nativeCrashReporting.constants';

type GlobalErrorHandler = (error: Error, isFatal?: boolean) => void;

interface ReactNativeErrorUtils {
  getGlobalHandler: () => GlobalErrorHandler | undefined;
  setGlobalHandler: (handler: GlobalErrorHandler) => void;
}

// ErrorUtils is a React Native global, NOT a named export of 'react-native'.
// Importing it from the module yields undefined at runtime and crashes on use.
function getErrorUtils(): ReactNativeErrorUtils | undefined {
  return (globalThis as unknown as { ErrorUtils?: ReactNativeErrorUtils }).ErrorUtils;
}

let sentryInitialized = false;
let priorGlobalHandler: GlobalErrorHandler | undefined;

function getSentry() {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require('@sentry/react-native') as typeof import('@sentry/react-native');
}

export function isNativeCrashReportingAllowed(): boolean {
  if (Platform.OS === 'web') return false;
  return getWebAnalyticsConsent() !== WEB_ANALYTICS_CONSENT_DENIED;
}

export function isNativeCrashReportingConfigured(): boolean {
  if (Platform.OS === 'ios' && IOS_DISABLED_NATIVE_SDKS.SENTRY) return false;
  return Platform.OS !== 'web' && getNativeCrashReportingDsn().length > 0;
}

export async function initNativeCrashReporting(): Promise<void> {
  if (Platform.OS === 'web') return;
  if (Platform.OS === 'ios' && IOS_DISABLED_NATIVE_SDKS.SENTRY) {
    await logLaunchPhase(LAUNCH_PHASE.SENTRY_INIT_SKIP, 'ios_disabled');
    return;
  }
  if (!isNativeCrashReportingConfigured()) {
    await logLaunchPhase(LAUNCH_PHASE.SENTRY_INIT_SKIP, 'not_configured');
    return;
  }
  if (!isNativeCrashReportingAllowed()) {
    await logLaunchPhase(LAUNCH_PHASE.SENTRY_INIT_SKIP, 'consent_denied');
    return;
  }
  if (sentryInitialized) return;

  try {
    await logLaunchPhase(LAUNCH_PHASE.SENTRY_INIT_START);
    getSentry().init({
      dsn: getNativeCrashReportingDsn(),
      environment: getAnalyticsEnvironment(),
      enableAutoSessionTracking: false,
      tracesSampleRate: 0,
    });

    try {
      const errorUtils = getErrorUtils();
      if (errorUtils) {
        priorGlobalHandler = errorUtils.getGlobalHandler();
        errorUtils.setGlobalHandler((error, isFatal) => {
          reportNativeError(error, { isFatal });
          priorGlobalHandler?.(error, isFatal);
        });
      }
    } catch (handlerError) {
      console.warn('[nativeCrashReporting] ErrorUtils handler install failed', handlerError);
    }

    sentryInitialized = true;
    await logLaunchPhase(LAUNCH_PHASE.SENTRY_INIT_OK);
  } catch (initError) {
    // A crash reporter must never crash the app — swallow and continue.
    console.warn('[nativeCrashReporting] Sentry init failed', initError);
  }
}

export function reportNativeError(
  error: Error,
  context?: Record<string, unknown>,
): void {
  if (Platform.OS === 'web') return;
  if (!isNativeCrashReportingConfigured() || !isNativeCrashReportingAllowed()) return;

  if (!sentryInitialized) {
    void initNativeCrashReporting();
  }

  try {
    getSentry().captureException(error, context ? { extra: context } : undefined);
  } catch (captureError) {
    console.warn('[nativeCrashReporting] captureException failed', captureError);
  }
}
